/**
 * PWA Offline — Dexie IndexedDB Database
 *
 * Database: ecliniq-offline v1
 *
 * Stores:
 * - appointments: Offline appointment cache per doctor (today + 7 days)
 * - pending_writes: Queued offline writes with FIFO replay
 * - sync_meta: Key-value store for sync metadata (lastSyncAt, deviceId)
 * - auth_tokens: AES-GCM encrypted auth tokens
 */

import Dexie, { type Table } from 'dexie';

// ── Type Definitions ──────────────────────────────────────────────────────────

export interface Appointment {
    id: number; // primary key
    doctor_id: number; // index
    date: string; // index (YYYY-MM-DD)
    start_time: string;
    end_time: string;
    patient_name: string;
    status: string; // index
    notes: string;
    synced_at: number; // Unix timestamp
}

export type PendingWriteStatus = 'pending' | 'syncing' | 'failed' | 'resolved';

export interface PendingWrite {
    id?: number; // autoIncrement primary key
    entity_type: string; // index ('consultation' | 'appointment')
    entity_id: number;
    payload: Record<string, unknown>;
    created_at: number; // index (Unix timestamp, used for FIFO)
    status: PendingWriteStatus;
    retry_count: number;
    error_reason?: string;
}

export interface SyncMeta {
    key: string; // primary key ('last_sync_timestamp', 'device_id')
    value: number;
}

export interface AuthToken {
    key: string; // primary key ('pwa_token', 'refresh_token')
    iv: string; // Base64 AES-GCM IV
    ciphertext: string; // Base64 encrypted token
    expires_at: number;
    user_id: number;
}

// ── Store Names ───────────────────────────────────────────────────────────────

export const APPOINTMENTS_STORE = 'appointments';
export const PENDING_WRITES_STORE = 'pending_writes';
export const SYNC_META_STORE = 'sync_meta';
export const AUTH_TOKENS_STORE = 'auth_tokens';

// ── Database Class ────────────────────────────────────────────────────────────

class EcliniqOfflineDatabase extends Dexie {
    appointments!: Table<Appointment, number>;
    pending_writes!: Table<PendingWrite, number>;
    sync_meta!: Table<SyncMeta, string>;
    auth_tokens!: Table<AuthToken, string>;

    constructor() {
        super('ecliniq-offline');

        this.version(1).stores({
            [APPOINTMENTS_STORE]: 'id, doctor_id, date, status',
            [PENDING_WRITES_STORE]: '++id, entity_type, created_at, status',
            [SYNC_META_STORE]: 'key',
            [AUTH_TOKENS_STORE]: 'key',
        });
    }
}

// ── Singleton Instance ─────────────────────────────────────────────────────────

export const db = new EcliniqOfflineDatabase();
